import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  Eye, 
  EyeOff, 
  ChevronUp, 
  ChevronDown,
  Palette,
  Type,
  ToggleLeft,
  ToggleRight,
  Plus,
  Trash2,
  AlertTriangle,
  Info
} from 'lucide-react';

const GlobalMenuConfig = () => {
  const [globalConfig, setGlobalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState(null);

  // Color options for menu items
  const colorOptions = [
    { value: 'text-blue-600', label: 'Blue', color: '#2563eb' },
    { value: 'text-green-600', label: 'Green', color: '#16a34a' },
    { value: 'text-purple-600', label: 'Purple', color: '#9333ea' },
    { value: 'text-yellow-600', label: 'Yellow', color: '#ca8a04' },
    { value: 'text-red-600', label: 'Red', color: '#dc2626' },
    { value: 'text-cyan-600', label: 'Cyan', color: '#0891b2' },
    { value: 'text-orange-600', label: 'Orange', color: '#ea580c' },
    { value: 'text-teal-600', label: 'Teal', color: '#0d9488' },
    { value: 'text-gray-600', label: 'Gray', color: '#4b5563' }
  ];

  // Icon options (commonly used icons)
  const iconOptions = [
    'Home', 'Users', 'UserPlus', 'Target', 'Activity', 'FileText', 'Receipt',
    'Brain', 'Settings', 'LayoutDashboard', 'Paperclip', 'StickyNote',
    'MessageSquare', 'Database', 'Library', 'Zap', 'HelpCircle', 'Building2',
    'Mail', 'BarChart3', 'Search', 'Filter', 'Download', 'Upload', 'Edit',
    'Trash2', 'Plus', 'Minus', 'Check', 'X', 'AlertTriangle', 'Info'
  ];

  useEffect(() => {
    loadGlobalConfig();
  }, []);

  const loadGlobalConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/menu-config/global', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setGlobalConfig(result.data);
      } else {
        throw new Error(result.message || 'Failed to load global menu configuration');
      }
    } catch (err) {
      console.error('Error loading global config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/menu-config/global', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(globalConfig)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        alert('Global menu configuration saved successfully!');
        setEditingItem(null);
        setNewItem(null);
      } else {
        throw new Error(result.message || 'Failed to save global menu configuration');
      }
    } catch (error) {
      console.error('Error saving global config:', error);
      alert('Error saving global menu configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleItemChange = (navType, itemId, field, value) => {
    setGlobalConfig(prev => ({
      ...prev,
      [navType]: {
        ...prev[navType],
        items: prev[navType].items.map(item => 
          item.id === itemId ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const handleAddItem = (navType) => {
    const newItem = {
      id: `new-item-${Date.now()}`,
      path: '/new-path',
      icon: 'Settings',
      labelKey: 'nav.newItem',
      color: 'text-gray-600',
      order: globalConfig[navType].items.length + 1,
      enabled: true,
      roles: [],
      description: 'New menu item'
    };

    setGlobalConfig(prev => ({
      ...prev,
      [navType]: {
        ...prev[navType],
        items: [...prev[navType].items, newItem]
      }
    }));

    setNewItem({ navType, itemId: newItem.id });
  };

  const handleDeleteItem = (navType, itemId) => {
    if (window.confirm('Are you sure you want to delete this menu item? This action cannot be undone.')) {
      setGlobalConfig(prev => ({
        ...prev,
        [navType]: {
          ...prev[navType],
          items: prev[navType].items.filter(item => item.id !== itemId)
        }
      }));
    }
  };

  const moveItem = (navType, itemId, direction) => {
    const items = [...globalConfig[navType].items];
    const currentIndex = items.findIndex(item => item.id === itemId);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Swap items
    [items[currentIndex], items[newIndex]] = [items[newIndex], items[currentIndex]];
    
    // Update order values
    items.forEach((item, index) => {
      item.order = index + 1;
    });
    
    setGlobalConfig(prev => ({
      ...prev,
      [navType]: {
        ...prev[navType],
        items: items
      }
    }));
  };

  const renderMenuItem = (item, navType) => {
    const isEditing = editingItem?.navType === navType && editingItem?.itemId === item.id;
    const isNew = newItem?.navType === navType && newItem?.itemId === item.id;

    return (
      <div key={item.id} className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-4 ${isEditing || isNew ? 'ring-2 ring-blue-500' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => moveItem(navType, item.id, 'up')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Move up"
                disabled={globalConfig[navType].items.indexOf(item) === 0}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                onClick={() => moveItem(navType, item.id, 'down')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Move down"
                disabled={globalConfig[navType].items.indexOf(item) === globalConfig[navType].items.length - 1}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">{item.id}</h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">({item.path})</span>
            {isNew && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">New</span>}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setEditingItem(isEditing ? null : { navType, itemId: item.id })}
              className={`px-3 py-1 rounded-md text-sm ${
                isEditing 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => handleDeleteItem(navType, item.id)}
              className="px-3 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-md text-sm hover:bg-red-200 dark:hover:bg-red-800"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        {(isEditing || isNew) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                ID
              </label>
              <input
                type="text"
                value={item.id}
                onChange={(e) => handleItemChange(navType, item.id, 'id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            {/* Path */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Path
              </label>
              <input
                type="text"
                value={item.path}
                onChange={(e) => handleItemChange(navType, item.id, 'path', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon
              </label>
              <select
                value={item.icon}
                onChange={(e) => handleItemChange(navType, item.id, 'icon', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                {iconOptions.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>

            {/* Label Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Label Key
              </label>
              <input
                type="text"
                value={item.labelKey}
                onChange={(e) => handleItemChange(navType, item.id, 'labelKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Color
              </label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map(colorOption => (
                  <button
                    key={colorOption.value}
                    onClick={() => handleItemChange(navType, item.id, 'color', colorOption.value)}
                    className={`w-8 h-8 rounded-full border-2 ${
                      item.color === colorOption.value ? 'border-gray-900 dark:border-white' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    style={{ backgroundColor: colorOption.color }}
                    title={colorOption.label}
                  />
                ))}
              </div>
            </div>

            {/* Enabled */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Enabled
              </label>
              <button
                onClick={() => handleItemChange(navType, item.id, 'enabled', !item.enabled)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm ${
                  item.enabled 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {item.enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                {item.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            {/* Roles */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Required Roles (comma-separated)
              </label>
              <input
                type="text"
                value={item.roles ? item.roles.join(', ') : ''}
                onChange={(e) => handleItemChange(navType, item.id, 'roles', e.target.value.split(',').map(r => r.trim()).filter(r => r))}
                placeholder="Admin, Manager"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={item.description || ''}
                onChange={(e) => handleItemChange(navType, item.id, 'description', e.target.value)}
                placeholder="Description of this menu item"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          </div>
        )}

        {/* Preview */}
        {showPreview && (
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</div>
            <div className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${item.color}`}>
              <span>{item.labelKey}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500 dark:text-gray-400">Loading global menu configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="text-red-800 dark:text-red-200">Error: {error}</div>
        <button 
          onClick={loadGlobalConfig}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!globalConfig) {
    return (
      <div className="text-gray-500 dark:text-gray-400">No global menu configuration available.</div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Global Menu Configuration
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage the global menu structure that serves as the base for all tenants.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm ${
                showPreview 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
              }`}
            >
              {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Important Notice
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
              Changes to the global menu configuration will affect all tenants. Make sure to test changes thoroughly before saving.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('main')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'main'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Main Navigation ({globalConfig.mainNavigation?.items?.length || 0} items)
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'admin'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Admin Navigation ({globalConfig.adminNavigation?.items?.length || 0} items)
            </button>
          </nav>
        </div>
      </div>

      {/* Menu Items */}
      <div className="space-y-6">
        {activeTab === 'main' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Main Navigation Items
              </h2>
              <button
                onClick={() => handleAddItem('mainNavigation')}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </button>
            </div>
            {globalConfig.mainNavigation?.items?.map(item => renderMenuItem(item, 'mainNavigation'))}
          </div>
        )}

        {activeTab === 'admin' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Admin Navigation Items
              </h2>
              <button
                onClick={() => handleAddItem('adminNavigation')}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Plus className="h-4 w-4" />
                Add Item
              </button>
            </div>
            {globalConfig.adminNavigation?.items?.map(item => renderMenuItem(item, 'adminNavigation'))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalMenuConfig;
